import React, { useState, useEffect } from 'react';
import { fetchInvoices, fetchInvoiceById, sendInvoice, fetchProfile as fetchProfileAPI } from '../utils/api';
import { formatDate } from '../utils/date';
import { ADMIN_ROLE, FINANCIAL_STAFF, FINANCIAL_VIEWER_STAFF } from '../utils/roles';
import InvoiceDetailModal from './InvoiceDetailModal';
import CreateInvoiceModal from './CreateInvoiceModal';
import SendInvoiceModal from './SendInvoiceModal';
import './Invoices.css';
import './_Pagination.css';
import './_Buttons.css';
import './_Input.css';
import './_Modal.css';

const Invoices = () => {
    const initialFromDate = new Date();
    initialFromDate.setMonth(initialFromDate.getMonth() - 1);
    const fromDateString = initialFromDate.toISOString().split('T')[0];

    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sortBy, setSortBy] = useState('issue_date');
    const [sortDir, setSortDir] = useState('desc');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [total, setTotal] = useState(0);
    const [amount, setAmount] = useState(0);
    const [from, setFrom] = useState(fromDateString);
    const [to, setTo] = useState('');
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isSendModalOpen, setIsSendModalOpen] = useState(false);
    const [invoiceToSend, setInvoiceToSend] = useState(null);
    const [showSentMessage, setShowSentMessage] = useState(false);
    const [profile, setProfile] = useState(null);

    const getInvoices = async () => {
        try {
            setLoading(true);
            const response = await fetchInvoices(sortBy, sortDir, page, limit, from, to);
            setInvoices(response.data.invoices || []);
            setTotal(response.data.total || 0);
            setAmount(response.data.amount || 0);
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const loadData = async () => {
            try {
                const profileData = await fetchProfileAPI();
                setProfile(profileData.data);
                getInvoices();
            } catch (error) {
                setError(error.message);
            }
        };

        loadData();
    }, [sortBy, sortDir, page, limit, from, to]);

    const handleSort = (newSortBy) => {
        setSortDir(sortBy === newSortBy && sortDir === 'asc' ? 'desc' : 'asc');
        setSortBy(newSortBy);
    };

    const handleInvoiceClick = async (invoiceId) => {
        try {
            const { data } = await fetchInvoiceById(invoiceId);
            setSelectedInvoice(data);
            setIsModalOpen(true);
        } catch (error) {
            setError(error.message);
        }
    };

    const handleSendInvoice = (e, invoiceId) => {
        e.stopPropagation();
        setInvoiceToSend(invoiceId);
        setIsSendModalOpen(true);
    };

    const closeSendModal = () => {
        setIsSendModalOpen(false);
        setInvoiceToSend(null);
    };

    const handleInvoiceSent = () => {
        closeSendModal();
        getInvoices();
        setShowSentMessage(true);
        setTimeout(() => {
            setShowSentMessage(false);
        }, 3000);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedInvoice(null);
    };

    const openCreateModal = () => {
        setIsCreateModalOpen(true);
    };

    const closeCreateModal = () => {
        setIsCreateModalOpen(false);
    };

    const handlePaymentRecorded = async () => {
        getInvoices();
        if (selectedInvoice) {
            const { data } = await fetchInvoiceById(selectedInvoice.id);
            setSelectedInvoice(data);
        }
    };

    const handleInvoiceDeleted = () => {
        getInvoices();
    };

    const handleLimitChange = (e) => {
        setLimit(parseInt(e.target.value, 10));
        setPage(1);
    };

    const totalPages = Math.ceil(total / limit) || 1;

    const canManageInvoices = profile && profile.roles.some(role => role.id === ADMIN_ROLE || role.id === FINANCIAL_STAFF);

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    return (
        <div className="invoices-container">
            {showSentMessage && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <p>Invoice sent successfully!</p>
                    </div>
                </div>
            )}
            <div className="header">
                <h1>Invoices | {total}</h1>
                <div className="header-controls">
                    <div className="showing-control">
                        <label htmlFor="limit">Showing:</label>
                        <select id="limit" value={limit} onChange={handleLimitChange}>
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                    {canManageInvoices && <button className="add-student-button" onClick={openCreateModal}>+</button>}
                </div>
            </div>
            <div className="invoices-filters">
                <div className='invoices-labels'>
                    <label>From: <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
                    <label>To: <input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
                </div>
                <div className="invoices-total-amount">
                    ${(amount / 100).toFixed(2)}
                </div>
            </div>
            <table className="invoices-table">
                <thead>
                    <tr>
                        <th onClick={() => handleSort('issue_date')}>
                            Issue Date {sortBy === 'issue_date' && (sortDir === 'asc' ? '▲' : '▼')}
                        </th>
                        <th onClick={() => handleSort('due_date')}>
                            Due Date {sortBy === 'due_date' && (sortDir === 'asc' ? '▲' : '▼')}
                        </th>
                        <th onClick={() => handleSort('name')}>
                            Name {sortBy === 'name' && (sortDir === 'asc' ? '▲' : '▼')}
                        </th>
                        <th>Student</th>
                        <th>Amount</th>
                        {canManageInvoices && <th>Actions</th>}
                    </tr>
                </thead>
                <tbody>
                    {invoices.map((invoice) => (
                        <tr key={invoice.id} onClick={() => handleInvoiceClick(invoice.id)} className="clickable-row">
                            <td>{formatDate(invoice.issue_date)}</td>
                            <td>{formatDate(invoice.due_date)}</td>
                            <td>{invoice.name}</td>
                            <td>{`${invoice.student.first_name} ${invoice.student.last_name}`}</td>
                            <td>${(invoice.total_amount / 100).toFixed(2)}</td>
                            {canManageInvoices && <td>
                                <button className='submit-button text-button' onClick={(e) => handleSendInvoice(e, invoice.id)}>Send</button>
                            </td>}
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan={canManageInvoices ? 6 : 5}>
                            <div className="pagination-controls">
                                <div className="page-buttons">
                                    <button onClick={() => handlePageChange(page - 1)} disabled={page === 1}>
                                        {'<'}
                                    </button>
                                    <span>Page {page} of {totalPages}</span>
                                    <button onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>
                                        {'>'}
                                    </button>
                                </div>
                            </div>
                        </td>
                    </tr>
                </tfoot>
            </table>
            <InvoiceDetailModal invoice={selectedInvoice} onClose={closeModal} onPaymentRecorded={handlePaymentRecorded} onInvoiceDeleted={handleInvoiceDeleted} canManage={canManageInvoices} />
            {isCreateModalOpen && <CreateInvoiceModal closeModal={closeCreateModal} refreshData={getInvoices} />}
            {isSendModalOpen && <SendInvoiceModal isOpen={isSendModalOpen} onClose={closeSendModal} sendRequest={() => sendInvoice(invoiceToSend)} onInvoiceSent={handleInvoiceSent} />}
        </div>
    );
};

export default Invoices;
